import isEqual from 'lodash/isEqual';
import { PosthogService } from '~/services/posthog';
import { buildUserProperties } from '~/sagas/sagaPosthog';

const STORAGE_KEY_PREFIX = 'posthog_identify_';

const getStorageKey = (userId: string) => `${STORAGE_KEY_PREFIX}${userId}`;

/**
 * Identify the user with PostHog only if their properties have changed
 * since the last identify call. This avoids redundant /decide API calls
 * when properties haven't changed (e.g. on page refresh).
 */
export const posthogIdentifyIfChanged = (
  user: any,
  childOffices?: unknown[] | null,
  // GOTU-4961: office source for office-derived props (see buildUserProperties). Pass
  // `selectedChildOffice ?? user` once /office has merged, or `null` before it's available.
  office = user,
): boolean => {
  if (!user?.id) return false;

  const currentProperties = buildUserProperties(user, childOffices, office);
  const storageKey = getStorageKey(user.id);

  try {
    const stored = localStorage.getItem(storageKey);
    const lastProperties = stored ? JSON.parse(stored) : null;

    if (lastProperties && isEqual(lastProperties, currentProperties)) {
      return false;
    }
  } catch {
    // Storage read failed — proceed with identify
  }

  PosthogService.getInstance().identify(user.id, currentProperties);

  try {
    localStorage.setItem(storageKey, JSON.stringify(currentProperties));
  } catch {
    // Storage write failed — non-critical
  }

  return true;
};

/**
 * Clear the cached identify properties on logout.
 */
export const clearPosthogIdentifyCache = (userId?: string) => {
  if (userId) {
    try {
      localStorage.removeItem(getStorageKey(userId));
    } catch {
      // Non-critical
    }
  }
};
